import { useMemo } from "react";

import { useGetScenarioResilienceScore } from "utils";

import { ScenarioData } from "pages/app/scenario-analysis/types/scenario";
import { isApiScenarioId } from "pages/app/scenario-analysis/utils/scenarioAnalysisHelpers";
import { ScenarioResilienceByScenarioId, buildResilienceInputFromYears } from "pages/app/scenario-analysis/utils/scenarioResilienceScore";

export interface UseScenarioResilienceScoresParams {
  scenarios: ScenarioData[];
  projectionYears: number;
  klimFarmId?: string;
  klimFieldId?: string | null;
  /** False while the connected field is missing/deleted, to keep the fetch from 403-ing. */
  isFieldValid?: boolean;
  /** False when nothing on the page consumes the scores, so a caller need not subscribe at all. */
  enabled?: boolean;
}

// One shared empty result, so memos downstream don't recompute while there are no scores.
export const NO_RESILIENCE_SCORES: ScenarioResilienceByScenarioId = {};

/** Resilience scores by scenario id; the query key is stable, so the chart and the cards share one fetch. */
export const useScenarioResilienceScores = ({
  scenarios,
  projectionYears,
  klimFarmId,
  klimFieldId,
  isFieldValid = true,
  enabled = true,
}: UseScenarioResilienceScoresParams) => {
  const resilienceInputs = useMemo(() => scenarios.map((scenario) => buildResilienceInputFromYears(scenario.id, scenario.years)), [scenarios]);
  const scenariosHydrated = scenarios.every((scenario) => isApiScenarioId(scenario.id));

  const {
    data: rawResilienceData = NO_RESILIENCE_SCORES,
    isPending: isResiliencePending,
    isError: isResilienceError,
    isFetching: isResilienceFetching,
  } = useGetScenarioResilienceScore({
    farmId: klimFarmId,
    fieldId: klimFieldId,
    projectionYears,
    scenarios: resilienceInputs,
    enabled: enabled && !!klimFarmId && scenariosHydrated && isFieldValid,
  });

  // Before hydration the ids are local, so the keyed results cannot be matched to a scenario.
  const resilienceData: ScenarioResilienceByScenarioId = scenariosHydrated ? rawResilienceData : NO_RESILIENCE_SCORES;

  return { resilienceData, isResiliencePending, isResilienceError, isResilienceFetching, scenariosHydrated };
};
